<template>
  <span :class="badgeClasses" :style="badgeStyles">
    <NvxIcon 
      v-if="icon" 
      :name="icon" 
      :size="iconSize"
      class="nvx-badge__icon"
    />
    <slot />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import NvxIcon from './NvxIcon.vue'

type BadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
type BadgeSize = 'xs' | 'sm' | 'md' | 'lg'

interface Props {
  variant?: BadgeVariant
  size?: BadgeSize
  icon?: string
  color?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'sm'
})

const badgeClasses = computed(() => [
  'nvx-badge',
  `nvx-badge--${props.variant}`,
  `nvx-badge--${props.size}`,
  {
    'nvx-badge--with-icon': props.icon
  }
])

const badgeStyles = computed(() => {
  if (props.color) {
    return {
      '--badge-color': props.color,
      '--badge-bg': props.color + '20',
      '--badge-border': props.color
    }
  }
  return {}
})

const iconSize = computed(() => {
  const sizeMap = {
    xs: 'xs',
    sm: 'xs',
    md: 'sm',
    lg: 'sm'
  }
  return sizeMap[props.size]
})
</script>

<style lang="scss" scoped>
.nvx-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--nvx-spacing-1);
  border-radius: var(--nvx-border-radius-full);
  font-weight: var(--nvx-font-weight-medium);
  white-space: nowrap;
  border: 1px solid transparent;

  // Sizes
  &--xs {
    padding: var(--nvx-spacing-1) var(--nvx-spacing-2);
    font-size: var(--nvx-font-size-xs);
    min-height: 16px;
  }

  &--sm {
    padding: var(--nvx-spacing-1) var(--nvx-spacing-1);
    font-size: xx-small;
    min-height: 20px;
  }

  &--md {
    padding: var(--nvx-spacing-2) var(--nvx-spacing-3);
    font-size: var(--nvx-font-size-sm);
    min-height: 24px;
  }

  &--lg {
    padding: var(--nvx-spacing-2) var(--nvx-spacing-4);
    font-size: var(--nvx-font-size-sm);
    min-height: 28px;
  }

  // Variants
  &--primary {
    background-color: var(--nvx-color-accent-primary);
    color: var(--nvx-color-text-primary);
    border-color: var(--nvx-color-accent-primary);
  }

  &--secondary {
    background-color: var(--nvx-color-background-secondary);
    color: var(--nvx-color-text-primary);
    border-color: var(--nvx-color-border-primary);
  }

  &--success {
    background-color: var(--nvx-color-status-success);
    color: var(--nvx-color-text-primary);
    border-color: var(--nvx-color-status-success);
  }

  &--warning {
    background-color: var(--nvx-color-status-warning);
    color: var(--nvx-color-text-primary);
    border-color: var(--nvx-color-status-warning-border);
  }

  &--error {
    background-color: var(--nvx-color-status-error);
    color: var(--nvx-color-text-primary);
    border-color: var(--nvx-color-status-error);
  }

  &--info {
    background-color: var(--nvx-color-status-info);
    color: var(--nvx-color-text-primary);
    border-color: var(--nvx-color-status-info);
  }

  &--with-icon {
    padding-left: var(--nvx-spacing-2);
  }

  &__icon {
    flex-shrink: 0;
  }

  // Custom color override
  &[style*="--badge-color"] {
    background-color: var(--badge-bg);
    color: var(--badge-color);
    border-color: var(--badge-border);
  }
}
</style>
